import React, {useMemo, useState} from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Tab,
    Tabs,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import {exportIcrCoverageHtml} from '../../../../firebase/firestore/icr';
import {ANALYTIC_TABS} from '../coverageModalUrlState';
import {openReportExcerptInNewTab} from '../navigateToReportExcerpt';
import TabLabelWithCount from '../components/TabLabelWithCount';
import {buildAnalyticCoverageView, isAnalyticNotApplicable} from './buildAnalyticCoverageView';
import AnalyticOverviewTab from './AnalyticOverviewTab';
import AnalyticQuestionsTab from './AnalyticQuestionsTab';
import AnalyticConflictsTab from './AnalyticConflictsTab';

/**
 * Analytic Coverage modal — Overview / Questions / Conflicts on the C1
 * question/AR snapshot. All open/tab/anchor state is URL-driven (P4 scheme:
 * ?coverage=analytic&tab=…&q=…&conflict=…) and wired by the parent.
 */
const AnalyticCoverageModal = ({
    open,
    onClose,
    snapshot,
    icrNumber,
    subject,
    icrId,
    tab = ANALYTIC_TABS.OVERVIEW,
    activeQuestionId = null,
    activeConflictId = null,
    onTabChange,
    onNavigateToConflict,
    onNavigateToQuestion,
    onRecompute,
    recomputing = false,
    isAdmin = false,
    canRecompute = false,
}) => {
    const [exporting, setExporting] = useState(false);
    const [exportError, setExportError] = useState(null);

    const view = useMemo(() => buildAnalyticCoverageView(snapshot), [snapshot]);
    const notApplicable = isAnalyticNotApplicable(snapshot);
    const overviewComputing = Boolean(
        (snapshot?.analyticCoverage?.computing ?? snapshot?.computing) || recomputing,
    );
    const canExport = isAdmin && Boolean(
        icrId
        && (snapshot?.analyticCoverage?.snapshotId ?? snapshot?.snapshotId)
        && (snapshot?.analyticCoverage?.overallPct ?? snapshot?.overallPct) != null,
    );
    const canRecomputeAction = Boolean(canRecompute && onRecompute && icrId);

    const handleExport = async () => {
        if (!canExport || exporting) {
            return;
        }
        setExportError(null);
        setExporting(true);
        try {
            await exportIcrCoverageHtml(icrId);
        } catch (err) {
            setExportError(err?.message || 'Export failed');
        } finally {
            setExporting(false);
        }
    };

    const renderOverview = () => {
        if (view || overviewComputing) {
            return <AnalyticOverviewTab view={view} computing={overviewComputing}/>;
        }
        if (notApplicable) {
            return (
                <Alert severity="info">
                    No questions could be scored for this ICR yet.
                </Alert>
            );
        }
        return <AnalyticOverviewTab view={null} computing={false}/>;
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{pr: 6}}>
                <Stack spacing={0.5}>
                    <Typography variant="h6" fontWeight={700}>
                        Analytic Coverage
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {icrNumber}{subject ? ` · ${subject}` : ''}
                    </Typography>
                </Stack>
                <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    sx={{position: 'absolute', right: 8, top: 8}}
                >
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>

            <Tabs
                value={tab}
                onChange={(_, next) => onTabChange?.(next)}
                sx={{px: 2, borderBottom: 1, borderColor: 'divider'}}
                variant="scrollable"
                scrollButtons="auto"
            >
                <Tab value={ANALYTIC_TABS.OVERVIEW} label="Overview"/>
                <Tab
                    value={ANALYTIC_TABS.QUESTIONS}
                    disabled={!view?.questions?.length}
                    label={(
                        <TabLabelWithCount
                            label="Questions"
                            count={view?.questions?.length ?? 0}
                            selected={tab === ANALYTIC_TABS.QUESTIONS}
                        />
                    )}
                />
                <Tab
                    value={ANALYTIC_TABS.CONFLICTS}
                    disabled={!view?.conflicts?.length}
                    label={(
                        <TabLabelWithCount
                            label="Conflicts"
                            count={view?.conflicts?.length ?? 0}
                            selected={tab === ANALYTIC_TABS.CONFLICTS}
                        />
                    )}
                />
            </Tabs>

            <DialogContent dividers>
                {exportError && (
                    <Alert severity="error" sx={{mb: 2}} onClose={() => setExportError(null)}>
                        {exportError}
                    </Alert>
                )}

                {tab === ANALYTIC_TABS.OVERVIEW && renderOverview()}
                {tab === ANALYTIC_TABS.QUESTIONS && (
                    <AnalyticQuestionsTab
                        view={view}
                        activeQuestionId={activeQuestionId}
                        onNavigateToConflict={onNavigateToConflict}
                        onOpenReport={openReportExcerptInNewTab}
                    />
                )}
                {tab === ANALYTIC_TABS.CONFLICTS && (
                    <AnalyticConflictsTab
                        view={view}
                        activeConflictId={activeConflictId}
                        onNavigateToQuestion={onNavigateToQuestion}
                        onOpenReport={openReportExcerptInNewTab}
                    />
                )}
            </DialogContent>

            {(canRecomputeAction || canExport) && (
                <DialogActions sx={{px: 2, py: 1.5, justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1}}>
                    {canRecomputeAction && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={recomputing
                                ? <CircularProgress size={16} color="inherit"/>
                                : <RefreshIcon/>}
                            disabled={recomputing || exporting}
                            aria-busy={recomputing}
                            onClick={() => {
                                if (!recomputing && !exporting) {
                                    onRecompute?.();
                                }
                            }}
                        >
                            {recomputing
                                ? ([
                                    snapshot?.progressLabel,
                                    snapshot?.progressDetail,
                                ].filter(Boolean).join(' — ') || 'Recomputing…')
                                : 'Recompute'}
                        </Button>
                    )}
                    {canExport && (
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={exporting ? <CircularProgress size={16}/> : <DownloadIcon/>}
                            disabled={exporting || recomputing}
                            onClick={handleExport}
                        >
                            {exporting ? 'Exporting…' : 'Export'}
                        </Button>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default AnalyticCoverageModal;
